import Decimal from 'decimal.js';
import { DeliveryState, PaymentState, Workflow } from '../../enums/orders';
import { CustomerType } from '../common/customer';
import { BranchType } from './branches';
import { CompactDriverType } from './drivers';
import { LabelKey, LabelValueWithKey } from './labels';
import { CompactMerchantType, MerchantType } from './merchants';
import { Money } from '../../utils/money';
import { ShopLocationType } from './pickups';
import { ILocation } from '../common/location';
import { IDeliveryBatch, IOrderDeliveryTask, IPickupTask } from './tasks';
import { IDriverInvoice } from '../common/driver-invoices';
import { IMerchantOrdersInvoice } from '../common/merchant-invoices';
import { IWarehouseShelf } from './warehouseshelf';
import { IDeliveryChargeRule } from '../../components/delivery-company-components/merchants/delivery-charge-rules/interfaces';

export interface Organisation {
  organisation_id: string;
  logoUrl?: string;
  brand?: string;
  custom_primary_color?: string;
}

export type NotificationStage = 'Pickup' | 'Delivery' | 'Payment' | 'Arrive At Warehouse';

export interface NotificationTemplate {
  name: string;
  template: string;
  orderWithinStages: Partial<Record<NotificationStage, number>>;
}


export enum CriticalOrderSurchargeMethod {
  FIXED_SURCHARGE = "FIXED_SURCHARGE",
  FIXED_FACTOR = "FIXED_FACTOR"
}

export interface OrganisationSettings {
  has_logo?: boolean;
  notification_templates: NotificationTemplate[];
  custom_primary_color: string;
  connected_phone_number?: string;
  branding_message:string;
  brand: string;
  navbar_logo: string | null;
  default_driver_commission_settlement_mode: 'NET' | 'GROSS';
  critical_order_surcharge_method?: CriticalOrderSurchargeMethod | null;
  critical_order_surcharge_fixed_amount?: Money[];
  critical_order_surcharge_factor: string | null;
  default_merchant_critical_order_monthly_credit?: number;
  invoice_frequency_enabled?: boolean;
  invoice_frequency_period_type?: string;
  invoice_frequency_limit?: number;
  invoice_frequency_charge_amount?: Money[];
  driver_location_tracking_enabled?: boolean;
}

export interface ExtraChargeSuggestions {
  [key: string]: ExtraChargeSuggestionDetails;
}

export interface ExtraChargeSuggestionDetails {
  should_apply: boolean;
  charge_amount: Money[];
  [key: string]: any; // Additional properties specific to each charge type
}

export interface InvoiceFrequencySuggestionDetails extends ExtraChargeSuggestionDetails {
  count_in_period: number;
  limit: number;
  period_type: string;
}

export interface ExtraCharge {
  reason: string;
  custom_reason?: string;
  amount: Money[];
}

export interface StateStamp {
  delivery_state: DeliveryState;
  payment_state: PaymentState;
  timestamp: string;
}

export interface Actor {
  id: number;
  name: string;
  organisation_id: string;
  phone_number: string;
}

export interface ActorSnapshot {
  id: string;
  name: string;
  phone_number: string;
  role: string;
}

export interface ChangeLog {
  new: string | null;
  old: string | null;
}

export type OrderFieldsChangeLogs = {
  [key: string]: ChangeLog;
};
export type PackageMetadata = {
  description?: string;
};
export interface OrderEvent {
  actor: Actor;
  actor_snapshot: ActorSnapshot;
  reason?: string;
  timestamp: string;
  changes: OrderFieldsChangeLogs;
}

export enum OrderSource {
  SOURCE_MANUAL = "MANUAL",
  SOURCE_SHOPIFY = "SHOPIFY",
  SOURCE_API_INTEGRATION = "API_INTEGRATION",
  SOURCE_EXCEL_IMPORT = "EXCEL_IMPORT",
  SOURCE_WOOCOMMERCE = "WOOCOMMERCE"
}

/**
 * Reroute modes for order rerouting.
 */
export enum RerouteMode {
  /** Deliver directly from current branch, skip any pending transfers */
  DIRECT_DELIVERY = "DIRECT_DELIVERY",
  /** Transfer to a specific branch, then deliver from there (no further transfers) */
  TRANSFER_TO_BRANCH = "TRANSFER_TO_BRANCH",
  /** Transfer to a specific branch, then calculate optimal route from there */
  TRANSFER_THEN_ROUTE = "TRANSFER_THEN_ROUTE",
}

/**
 * Represents a leg in an order's route - a single hop between two branches.
 */
export interface RouteLeg {
  id: number;
  position: number;
  source: BranchType;
  destination: BranchType;
  travel_minutes: number | null;
  status: 'awaiting' | 'in_transit' | 'finished';
}

export interface OrderType {
  id: string;
  branch: BranchType | null;
  customer: CustomerType | null;
  driver: CompactDriverType | null;
  merchant: CompactMerchantType | null;
  shelf: IWarehouseShelf | null;
  order_id: string;
  reference_id: string;
  tracking_hash: string;
  creation_date?: string;
  created_by?: ActorSnapshot;
  customer_location: ILocation | null;
  governorate: string;
  district: string;
  number_of_packages: number;
  description: string;
  packages_metadata?: PackageMetadata[];
  private_note: string;
  public_note: string;
  return_reason: string;
  task_notes?: string[];
  delivery_state: DeliveryState;
  payment_state: PaymentState;
  workflow: Workflow;
  statestamps?: StateStamp[];
  events?: OrderEvent[];
  labels?: LabelValueWithKey[];
  organisation?: Organisation;
  delivery_fee_covered_by_merchant: boolean;
  driver_invoice?: IDriverInvoice;
  merchant_invoice?: IMerchantOrdersInvoice;
  price: Money[];
  pop: Money[];
  delivery_fee: Money[];
  driver_commission: Money[];
  merchant_location: ShopLocationType | null;
  dimensions: Decimal[];
  weight: Decimal;
  last_pickup_task?: IPickupTask; // id of task
  last_delivery_task?: IOrderDeliveryTask; // id of task
  pickup_tasks?: IPickupTask[];
  delivery_tasks?: IOrderDeliveryTask[];
  is_critical: boolean;
  is_exchange: boolean;
  delivered_at?: string;
  snoozed_until?: string;
  frozen_until?: string;
  frozen_because?: string;
  is_frozen?: boolean;
  is_fragile?: boolean;
  invoice?: string;
  delivery_attempts_count?: number;
  delivery_fee_rules?: IDeliveryChargeRule[];
  exchange_for?: number;
  updated_by_us?: boolean;
  source: OrderSource;
  product_price?: string;
  pickup_request_stamp?: string | null;
  /** The next leg in the route that the order needs to complete (transfer to another branch) */
  next_leg?: RouteLeg | null;
  /** Whether the order needs to be transferred to another branch before delivery */
  needs_transfer?: boolean;

  // read-only field
  critical_order_surcharge?: CriticalOrderSurcharge
}

export interface CreateOrderResponse {
  message: string;
  statusCode: number;
}


export interface ShopifyOrderValidationResult {
  "shopify_order_url": string;
  "shopify_order_id": string;
  "order_number": string;
  "errors": string[];
  "last_validation_at": string;
}

export interface BranchRecommendation {
  id: number;
  name: string;
  letter: string | null;
}

export interface BranchRecommendationResponse {
  recommended_branch: BranchRecommendation | null;
  method: 'cover' | 'tol_gap' | 'none' | 'default';
  explanation: string;
  context_branch: BranchRecommendation | null;
}

export interface CriticalOrderSurcharge {
  method: CriticalOrderSurchargeMethod;
  amount: Money[]
}

export interface CriticalOrderBalance {
  effective_monthly_credit: number;
  current_balance: number;
  remaining_credit: number;
  will_incur_surcharge: boolean;
  surcharge_method: CriticalOrderSurchargeMethod,
  estimated_surcharge: Money[]
}